import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X, Image as ImageIcon, AlertCircle, Loader2, ZoomIn, Pencil, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { properties } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/hooks/use-toast';

interface PropertyImageGalleryProps {
  propertyId: string;
  images: string[];
  imageCaptions?: Record<string, string>;
  maxImages?: number;
  maxSizeMB?: number;
  className?: string;
}

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export function PropertyImageGallery({
  propertyId,
  images,
  imageCaptions = {},
  maxImages = 10,
  maxSizeMB = MAX_FILE_SIZE_MB,
  className,
}: PropertyImageGalleryProps) {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Caption editing state
  const [editingCaption, setEditingCaption] = useState<string | null>(null); // image key
  const [captionText, setCaptionText] = useState('');

  // Upload mutation — NO invalidation here, batch invalidation in handleFiles
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadingFiles(prev => new Set(prev).add(file.name));
      return properties.uploadImage(token!, propertyId, file);
    },
    onError: (_error: Error, file: File) => {
      setUploadingFiles(prev => { const s = new Set(prev); s.delete(file.name); return s; });
      // Remove preview for failed upload
      setPreviewUrls(prev => { const m = new Map(prev); URL.revokeObjectURL(m.get(file.name) || ''); m.delete(file.name); return m; });
    },
    onSettled: (_, __, file: File) => {
      setUploadingFiles(prev => { const s = new Set(prev); s.delete(file.name); return s; });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => properties.deleteImage(token!, propertyId, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({ title: 'Mynd eytt', description: 'Myndin hefur verið fjarlægð.', variant: 'success' });
    },
    onError: (error) => {
      toast({ title: 'Villa', description: error instanceof Error ? error.message : 'Ekki tókst að eyða mynd.', variant: 'destructive' });
    },
  });

  // Save caption mutation
  const saveCaptionMutation = useMutation({
    mutationFn: async ({ key, caption }: { key: string; caption: string }) => {
      const updatedCaptions = { ...imageCaptions, [key]: caption };
      if (!caption.trim()) delete updatedCaptions[key];
      return properties.update(token!, propertyId, { image_captions: updatedCaptions } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setEditingCaption(null);
      toast({ title: 'Texti vistaður' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að vista texta.', variant: 'destructive' });
    },
  });

  // Validate file
  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'Óleyft skráarsnið. Leyfð snið: JPG, PNG, WebP';
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) return `Skrá er of stór (${sizeMB.toFixed(1)} MB). Hámark: ${maxSizeMB} MB`;
    if (images.length >= maxImages) return `Hámarksfjöldi mynda náð (${maxImages})`;
    return null;
  };

  // Handle file selection — upload sequentially, invalidate ONCE after all
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    const fileArray = Array.from(files);
    const remainingSlots = maxImages - images.length;
    const filesToProcess = fileArray.slice(0, remainingSlots);
    const totalToProcess = filesToProcess.length;

    // Create optimistic preview URLs immediately
    for (const file of filesToProcess) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewUrls(prev => new Map(prev).set(file.name, previewUrl));
    }

    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      const validationError = validateFile(file);
      if (validationError) { setError(validationError); failCount++; continue; }

      // Show progress toast for multi-upload
      if (totalToProcess > 1) {
        toast({ title: 'Hleð upp...', description: `Mynd ${i + 1} af ${totalToProcess}...` });
      }

      try {
        await uploadMutation.mutateAsync(file);
        successCount++;
      } catch {
        failCount++;
      }
    }

    // Clear all preview URLs (real images will load from server)
    setPreviewUrls(prev => {
      prev.forEach(url => URL.revokeObjectURL(url));
      return new Map();
    });

    // BATCH invalidation — ONE refetch after ALL uploads
    if (successCount > 0) {
      await queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    }

    // Final toast
    if (fileArray.length > remainingSlots) {
      setError(`Aðeins ${remainingSlots} myndir voru hlaðið upp (hámark: ${maxImages})`);
    }
    if (failCount > 0 && successCount > 0) {
      toast({ title: 'Upphleðslu lokið', description: `${successCount} myndir hlaðið upp, ${failCount} mistókust.`, variant: 'default' });
    } else if (successCount > 0) {
      toast({ title: 'Myndum hlaðið upp', description: `${successCount} ${successCount === 1 ? 'mynd hlaðin' : 'myndir hlaðnar'} upp.`, variant: 'success' });
    } else if (failCount > 0) {
      toast({ title: 'Villa', description: 'Ekki tókst að hlaða upp myndum.', variant: 'destructive' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); handleFiles(e.dataTransfer.files); };

  const handleRemove = (key: string) => { deleteMutation.mutate(key); };
  const handleClick = () => { fileInputRef.current?.click(); };

  // Start editing caption
  const startEditCaption = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCaption(key);
    setCaptionText(imageCaptions[key] || '');
  };

  // Save caption
  const saveCaption = (key: string) => {
    saveCaptionMutation.mutate({ key, caption: captionText });
  };

  // Lightbox navigation
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => { if (lightboxIndex !== null) setLightboxIndex((lightboxIndex + 1) % images.length); };
  const prevImage = () => { if (lightboxIndex !== null) setLightboxIndex((lightboxIndex - 1 + images.length) % images.length); };

  // Keyboard navigation for lightbox
  const handleLightboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  const canUploadMore = images.length < maxImages;
  const isUploading = uploadingFiles.size > 0;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload Area */}
      {canUploadMore && (
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer',
            'min-h-[120px] flex flex-col items-center justify-center gap-3',
            isDragging ? 'border-prima-500 bg-prima-50' : 'border-gray-300 hover:border-prima-400 hover:bg-gray-50',
            isUploading && 'opacity-50 pointer-events-none'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleInputChange}
            className="hidden"
            disabled={isUploading}
          />
          <div className="flex flex-col items-center gap-2">
            {isUploading ? <Loader2 className="w-8 h-8 text-prima-600 animate-spin" /> : <Upload className="w-8 h-8 text-gray-400" />}
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                {isUploading ? 'Hleð upp...' : 'Smelltu til að velja myndir eða dragðu hingað'}
              </p>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG eða WebP (hámark {maxSizeMB} MB)</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{images.length} af {maxImages} myndum</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Image Previews Grid */}
      {(images.length > 0 || previewUrls.size > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((key, index) => {
            const imageUrl = properties.getImageUrl(propertyId, key);
            const caption = imageCaptions[key];
            const isEditingThis = editingCaption === key;

            return (
              <div
                key={key}
                className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 transition-transform hover:scale-[1.02]"
              >
                <img
                  src={imageUrl}
                  alt={caption || `Mynd ${index + 1}`}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="lazy"
                  onClick={() => openLightbox(index)}
                />

                {/* Hover overlay with caption */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                {/* Caption display on hover */}
                {caption && !isEditingThis && (
                  <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-white font-medium truncate">{caption}</p>
                  </div>
                )}

                {/* Caption edit inline */}
                {isEditingThis && (
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/70 backdrop-blur-sm" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={captionText}
                        onChange={e => setCaptionText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveCaption(key); if (e.key === 'Escape') setEditingCaption(null); }}
                        placeholder="Texti á mynd..."
                        className="flex-1 text-xs px-2 py-1 rounded bg-white/90 text-gray-900 outline-none min-w-0"
                        autoFocus
                      />
                      <button
                        onClick={() => saveCaption(key)}
                        className="p-1 rounded bg-green-500 text-white hover:bg-green-600"
                        disabled={saveCaptionMutation.isPending}
                      >
                        {saveCaptionMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Primary Badge */}
                {index === 0 && (
                  <div className="absolute top-2 left-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-prima-600 text-white shadow-sm">
                      <ImageIcon className="w-3 h-3" />
                      Aðalmynd
                    </span>
                  </div>
                )}

                {/* Action buttons (hover) */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Zoom button */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                    className="p-1.5 rounded-full bg-white/90 text-gray-700 shadow-md hover:bg-white min-h-[36px] min-w-[36px] flex items-center justify-center"
                    aria-label={`Stækka mynd ${index + 1}`}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>

                  {/* Caption edit button */}
                  <button
                    type="button"
                    onClick={(e) => startEditCaption(key, e)}
                    className="p-1.5 rounded-full bg-white/90 text-gray-700 shadow-md hover:bg-white min-h-[36px] min-w-[36px] flex items-center justify-center"
                    aria-label="Breyta texta"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRemove(key); }}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 min-h-[36px] min-w-[36px] flex items-center justify-center disabled:opacity-50"
                    aria-label={`Eyða mynd ${index + 1}`}
                  >
                    {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  </button>
                </div>

                {/* Image Number */}
                <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded text-xs text-white group-hover:opacity-0 transition-opacity">
                  {index + 1}
                </div>
              </div>
            );
          })}

          {/* Optimistic preview thumbnails for uploading files */}
          {Array.from(previewUrls.entries()).map(([name, url]) => (
            <div
              key={`preview-${name}`}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-prima-300 animate-pulse"
            >
              <img
                src={url}
                alt={`Hleð upp: ${name}`}
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
              <div className="absolute bottom-2 left-2 right-2">
                <p className="text-xs text-white bg-black/50 px-2 py-1 rounded truncate">{name}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox / Full-screen Preview */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleLightboxKeyDown}
          tabIndex={0}
          role="dialog"
          aria-label="Myndaskoðari"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30 z-10 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Loka"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 text-white hover:bg-white/30 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fyrri mynd"
              >
                <span className="text-2xl font-light">‹</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 text-white hover:bg-white/30 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Næsta mynd"
              >
                <span className="text-2xl font-light">›</span>
              </button>
            </>
          )}

          {/* Main image */}
          <img
            src={properties.getImageUrl(propertyId, images[lightboxIndex])}
            alt={imageCaptions[images[lightboxIndex]] || `Mynd ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Caption and counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
            {imageCaptions[images[lightboxIndex]] && (
              <p className="text-white text-sm mb-2 bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm">
                {imageCaptions[images[lightboxIndex]]}
              </p>
            )}
            <p className="text-white/70 text-xs">
              {lightboxIndex + 1} / {images.length}
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && !canUploadMore && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ImageIcon className="w-12 h-12 mb-3" />
          <p className="text-sm">Engar myndir</p>
        </div>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800">
          <p className="font-medium mb-1">Ábending:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Smelltu á mynd til að stækka. Notaðu örvalykla til að fletta.</li>
            <li>Smelltu á blýantinn til að bæta texta við mynd.</li>
            <li>Fyrsta myndin verður aðalmyndin sem birtist í listanum.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
